import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { AttendanceFaceEnrollmentStatus } from 'shared-models/browser';
import { attendanceService } from '../../../../services/attendance/attendance.service';
import { useEmployeeSession } from '../../../../contexts/EmployeeSessionProvider';

export function useAttendanceFaceEnrollment(employeeId: number, onClose: () => void) {
  const { employeeState } = useEmployeeSession();
  const tenantId = employeeState?.employee.tenantId ?? '';
  const session = employeeState?.employeeSession ?? '';
  const [status, setStatus] = useState<AttendanceFaceEnrollmentStatus | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    let active = true; mounted.current = true;
    setLoading(true);
    void attendanceService.faceStatus(tenantId, employeeId, session).then((value) => {
      if (active) setStatus(value);
    }).catch(() => { if (active) setError('לא ניתן לטעון את רישום הפנים'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; mounted.current = false; };
  }, [tenantId, employeeId, session]);

  const mutate = async (operation: () => Promise<void>, closeAfterEnrollment = false): Promise<boolean> => {
    if (lock.current || loading || !status?.enabled) return false;
    lock.current = true; setBusy(true); setError('');
    let succeeded = false;
    let confirmedEnrolled = false;
    try { await operation(); succeeded = true; if (mounted.current) setConsent(false); }
    catch (failure) { if (mounted.current) setError(failure instanceof Error ? failure.message : 'הפעולה נכשלה'); }
    finally {
      try {
        const canonical = await attendanceService.faceStatus(tenantId, employeeId, session);
        confirmedEnrolled = canonical.enrolled;
        if (mounted.current) setStatus(canonical);
      } catch { if (mounted.current) setError('לא ניתן לאמת את מצב רישום הפנים. יש לסגור ולפתוח שוב'); }
      lock.current = false; if (mounted.current) setBusy(false);
    }
    if (succeeded && closeAfterEnrollment && confirmedEnrolled && mounted.current) {
      notifications.show({ color: 'green', message: 'סריקת פנים נשמרה בהצלחה' });
      onClose();
    }
    return succeeded;
  };
  return { status, consent, setConsent, busy, loading, error,
    close: () => { if (!lock.current) onClose(); },
    enroll: (imageDataUrl: string) => consent ? mutate(() => attendanceService.enrollFace(tenantId, employeeId, session,
      { imageDataUrl, consentConfirmed: true }), true) : Promise.resolve(false),
    revoke: () => {
      if (lock.current || !window.confirm('למחוק את תמונת הפנים ולבטל את הרישום לנוכחות?')) return Promise.resolve(false);
      return mutate(() => attendanceService.revokeFace(tenantId, employeeId, session));
    },
  };
}